import {
  isStaanderPanelDoor,
  standingDoorBeamInsetMm,
  standingPanelBeamInsetMm,
  standingProfileWidthMm,
} from "./layoutGeometry";
import {
  mirrorVerticalBeamPosition,
  type VerticalBeamPosition,
} from "./layoutMeta";
import { standingDoorMinWidth, standingPanelMinWidth } from "./widthLimits";

export const staanderPositions: readonly VerticalBeamPosition[] = [
  "left",
  "middle",
  "right",
];
const supportedPresetCodes = ["populair_1", "populair_2"];
const classicStaanderLayouts = new Set(["dekaap", "deblaak", "demeent"]);
const supportedDoors = new Set([
  "opdekdeur",
  "stompedeur",
  "opdekdeurmetkozijn",
  "scharnierdeur",
  "dubbelescharnierdeur",
  "taatsdeur",
  "dubbel",
  "schuifdeur",
  "dubbeleschuifdeur",
  "paneel",
  "paneelpaneel",
]);
const doubleDoors = new Set([
  "dubbelescharnierdeur",
  "dubbel",
  "dubbeleschuifdeur",
  "paneelpaneel",
]);

export const supportsStaander = (
  layoutCode: string | null | undefined,
  presetCode: string | null | undefined,
  doorCode: string | undefined,
): boolean => {
  if (!doorCode || !supportedDoors.has(doorCode)) return false;

  const isClassicLayout =
    !!layoutCode && classicStaanderLayouts.has(layoutCode);
  const isEligibleEigenwijsPreset =
    layoutCode === "eigenwijs" &&
    !!presetCode &&
    supportedPresetCodes.includes(presetCode);

  return isClassicLayout || isEligibleEigenwijsPreset;
};

export const resolveSceneStaanderPosition = ({
  isScreenshot,
  doorCode,
  layoutCode,
  presetCode,
  requestedPosition,
  selectedPosition,
}: {
  isScreenshot: boolean;
  doorCode: string | undefined;
  layoutCode: string | null | undefined;
  presetCode: string | null | undefined;
  requestedPosition: VerticalBeamPosition | undefined;
  selectedPosition: VerticalBeamPosition | undefined;
}): VerticalBeamPosition | undefined => {
  // Confi supplies the current drawing's beams, independently of webshop presets.
  const restoresCustomScreenshot =
    isScreenshot &&
    layoutCode === "eigenwijs" &&
    presetCode === "zelfkiezen" &&
    !!doorCode &&
    supportedDoors.has(doorCode);

  return restoresCustomScreenshot ? requestedPosition : selectedPosition;
};

export const isVerticalProfileExtra = (code: string): boolean =>
  code === "verticalprofile1" || code === "verticalprofile1dubbel";

export const getVerticalProfileExtraCode = (doorCode: string): string =>
  doubleDoors.has(doorCode) ? "verticalprofile1dubbel" : "verticalprofile1";

export const parseStaander = (
  value: string | null,
): VerticalBeamPosition | undefined =>
  staanderPositions.find((position) => position === value);

const verticalBeamsForInset = (
  widths: number[],
  position: VerticalBeamPosition,
  inset: number,
): number[][] => {
  const halfProfileWidth = standingProfileWidthMm / 2;

  return widths.map((width, index) => {
    const leafPosition =
      index === 1 && widths.length === 2
        ? (mirrorVerticalBeamPosition(position) as VerticalBeamPosition)
        : position;

    if (leafPosition === "middle") return [width / 2];
    const center =
      leafPosition === "left"
        ? inset - halfProfileWidth
        : width - inset + halfProfileWidth;
    return [Math.ceil(center)];
  });
};

export const getStaanderVerticalBeams = (
  widths: number[],
  doorCode: string,
  position: VerticalBeamPosition,
): number[][] =>
  verticalBeamsForInset(
    widths,
    position,
    isStaanderPanelDoor(doorCode)
      ? standingPanelBeamInsetMm
      : standingDoorBeamInsetMm,
  );

export const findStaander = (
  widths: number[] | undefined,
  verticalBeams: number[][] | undefined,
  doorCode?: string,
): VerticalBeamPosition | undefined => {
  if (!widths?.length || verticalBeams?.length !== widths.length) {
    return undefined;
  }

  const insets: number[] = [];
  if (doorCode) {
    insets.push(
      isStaanderPanelDoor(doorCode)
        ? standingPanelBeamInsetMm
        : standingDoorBeamInsetMm,
    );
  } else {
    if (widths.every((width) => width >= standingDoorMinWidth)) {
      insets.push(standingDoorBeamInsetMm);
    }
    if (widths.every((width) => width >= standingPanelMinWidth)) {
      insets.push(standingPanelBeamInsetMm);
    }
  }

  return staanderPositions.find((position) =>
    insets.some((inset) =>
      verticalBeamsForInset(widths, position, inset).every(
        ([expected], index) =>
          verticalBeams[index].length === 1 &&
          verticalBeams[index][0] === expected,
      ),
    ),
  );
};
